import { useEffect, useState } from "react";

// Eyebrow text for specific events, keyed by Bandsintown event id. The
// event's own title field has changed shape a few times already (with/without
// asterisks, artist name only), so for shows where the exact label matters,
// set it here instead of depending on that field's formatting.
const EYEBROW_OVERRIDES = {
  "108749591": "Album Release Show",
};

// Same idea as EYEBROW_OVERRIDES, but keyed by the event's ISO date instead
// of its Bandsintown id — for shows added via bulk upload before their real
// event id has been looked up.
const EYEBROW_OVERRIDES_BY_DATE = {
  "2026-09-14": "w/ Psychedelic Porn Crumpets",
  "2026-09-16": "w/ Psychedelic Porn Crumpets",
  "2026-09-17": "w/ Psychedelic Porn Crumpets",
  "2026-09-20": "w/ Psychedelic Porn Crumpets",
  "2026-09-22": "w/ Psychedelic Porn Crumpets",
  "2026-09-23": "w/ Psychedelic Porn Crumpets",
  "2026-09-24": "w/ Psychedelic Porn Crumpets",
  "2026-09-25": "w/ Psychedelic Porn Crumpets",
};

function parseEvent(event) {
  // Falls back to whatever's left of the event title after the artist name
  // for any event without an explicit override above.
  const artistName = (event.artist && event.artist.name) || "";
  let notePart = (event.title || "").replace(/\*/g, "").trim();
  if (artistName && notePart.toLowerCase().startsWith(artistName.toLowerCase())) {
    notePart = notePart.slice(artistName.length).trim();
  }
  const eventDate = (event.datetime || "").slice(0, 10);
  const eyebrow =
    EYEBROW_OVERRIDES[event.id] ||
    EYEBROW_OVERRIDES_BY_DATE[eventDate] ||
    notePart ||
    null;

  const venueName = event.venue.name.replace(/\*/g, "").trim();
  const venueLine = event.venue.region
    ? `${venueName} - ${event.venue.city}, ${event.venue.region}`
    : `${venueName} - ${event.venue.city}`;

  const dt = new Date(event.datetime);
  const dateLine = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(dt);
  const timeLine = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(dt);

  const ticketUrl =
    event.offers && event.offers.length > 0 ? event.offers[0].url : event.url;
  const hasTickets = Boolean(event.offers && event.offers.length > 0);

  return {
    id: event.id,
    eyebrow,
    venueLine,
    dateLine,
    timeLine,
    ticketUrl,
    hasTickets,
    isSoldOut: Boolean(event.sold_out),
  };
}

// IntegralCF (the display font used for the venue line) is a demo/trial
// font: it's missing glyphs for punctuation like "'", "/", and "-", and
// renders a decorative placeholder glyph in their place instead. Rather than
// stripping those characters, split runs of punctuation out into a span
// using the site's normal body font, which has full glyph coverage.
function renderWithFallbackFont(text) {
  const parts = text.match(/[A-Za-z0-9\s]+|[^A-Za-z0-9\s]+/g) || [];
  return parts.map((part, i) =>
    /^[A-Za-z0-9\s]+$/.test(part) ? (
      part
    ) : (
      <span key={i} className="font-monda">
        {part}
      </span>
    )
  );
}

const ShowCard = ({ show }) => (
  <div className="border border-coolPaper/20 rounded-lg p-6 flex flex-col md:flex-row items-center justify-between gap-4 bg-black/20">
    <div className="text-center md:text-left">
      {show.eyebrow && (
        <p className="text-rust text-xs uppercase tracking-widest font-bold mb-1">
          {show.eyebrow}
        </p>
      )}
      <p className="font-integralCF text-2xl md:text-3xl mb-1">
        {renderWithFallbackFont(show.venueLine)}
      </p>
      <p className="font-monda text-coolPaper/70">
        {show.dateLine} · {show.timeLine}
      </p>
    </div>
    {show.isSoldOut ? (
      <button
        disabled
        className="bg-coolPaper/10 text-coolPaper/50 font-bold uppercase tracking-wide text-sm py-3 px-6 rounded whitespace-nowrap cursor-not-allowed"
      >
        Sold Out
      </button>
    ) : (
      <a href={show.ticketUrl} target="_blank" rel="noopener noreferrer">
        <button className="bg-rust hover:bg-rustDeep text-coolPaper font-bold uppercase tracking-wide text-sm py-3 px-6 rounded transition-colors whitespace-nowrap">
          {show.hasTickets ? "Get Tickets" : "More Info"}
        </button>
      </a>
    )}
  </div>
);

const LiveShows = () => {
  const [shows, setShows] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch("/api/bandsintown-events")
      .then((res) => {
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        return res.json();
      })
      .then((events) => setShows(events.map(parseEvent)))
      .catch(() => setError(true));
  }, []);

  if (error || (shows && shows.length === 0)) {
    return null;
  }

  if (!shows) {
    return null;
  }

  return (
    <div className="w-full max-w-3xl px-4 mb-10 space-y-4">
      {shows.map((show) => (
        <ShowCard key={show.id} show={show} />
      ))}
    </div>
  );
};

export default LiveShows;
